/**
 * Sistema de Animações Centralizado
 * Gerencia animações de surgimento baseadas em Intersection Observer
 */

(function() {
  'use strict';

  // Verificar se o usuário prefere movimento reduzido
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Inicializa animação para um elemento específico
   * @param {string} selector - Seletor CSS do elemento
   * @param {Object} options - Opções de configuração
   * @param {Function} [options.onVisible] - Callback quando elemento fica visível
   * @param {string} [options.animatedClass] - Classe que indica que o elemento está animado
   */
  function initAnimation(selector, options = {}) {
    const element = document.querySelector(selector);
    const animatedClass = options.animatedClass || '--animated';
    
    if (!element || !element.className.includes(animatedClass)) {
      return;
    }

    // Se usuário prefere movimento reduzido, mostrar imediatamente
    if (prefersReducedMotion) {
      element.classList.add('is-visible');
      if (options.onVisible) {
        options.onVisible(element);
      }
      return;
    }

    // Função para verificar se está visível e animar
    function checkAndAnimate() {
      const rect = element.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (isVisible) {
        // Se já está visível, animar imediatamente
        element.classList.add('is-visible');
        if (options.onVisible) {
          options.onVisible(element);
        }
        return true;
      }
      return false;
    }

    // Verificar imediatamente
    if (checkAndAnimate()) {
      return;
    }

    // Intersection Observer para detectar quando o elemento está visível
    const observerOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0.01
    };

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          if (options.onVisible) {
            options.onVisible(entry.target);
          }
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    observer.observe(element);
  }

  /**
   * Inicializa animação de divider
   * @param {string} selector - Seletor CSS do divider line
   */
  function initDividerAnimation(selector) {
    initAnimation(selector, {
      animatedClass: 'divider__line--animated'
    });
  }

  /**
   * Inicializa animação de typewriter
   * @param {string} selector - Seletor CSS do elemento
   * @param {Object} options - Opções do typewriter
   * @param {number} options.speed - Velocidade em ms entre cada letra
   * @param {number} options.delay - Delay antes de iniciar
   */
  function initTypewriter(selector, options = {}) {
    const element = document.querySelector(selector);
    if (!element) return;

    const speed = options.speed || 30;
    const delay = options.delay || 0;
    const originalText = element.textContent || element.innerText;
    const originalHTML = element.innerHTML;

    // Limpar o conteúdo
    element.innerHTML = '';
    element.style.opacity = '1';

    // Adicionar letras uma por uma
    let currentIndex = 0;
    
    function addNextChar() {
      if (currentIndex >= originalText.length) {
        // Restaurar HTML original
        element.innerHTML = originalHTML;
        element.classList.add('typewriter-complete');
        return;
      }

      const char = originalText[currentIndex];
      const textNode = document.createTextNode(char);
      element.appendChild(textNode);

      currentIndex++;
      setTimeout(addNextChar, speed);
    }

    // Iniciar após o delay
    setTimeout(addNextChar, delay);
  }

  /**
   * Inicializa múltiplas animações de typewriter em uma lista
   * @param {string} selector - Seletor CSS base (deve retornar múltiplos elementos)
   * @param {Object} options - Opções do typewriter
   */
  function initTypewriterList(selector, options = {}) {
    const elements = document.querySelectorAll(selector);
    if (!elements.length) return;

    const speed = options.speed || 30;
    const delay = options.delay || 0;
    const staggerDelay = options.staggerDelay || 200; // Delay entre cada elemento

    elements.forEach((element, index) => {
      const originalText = element.getAttribute('data-original-text') || element.textContent || element.innerText;
      const originalHTML = element.getAttribute('data-original-html') || element.innerHTML;

      // Limpar o conteúdo
      element.innerHTML = '';
      element.style.opacity = '1';

      // Adicionar letras uma por uma
      let currentIndex = 0;
      
      function addNextChar() {
        if (currentIndex >= originalText.length) {
          // Restaurar HTML original
          element.innerHTML = originalHTML;
          element.classList.add('typewriter-complete');
          return;
        }

        const char = originalText[currentIndex];
        const textNode = document.createTextNode(char);
        element.appendChild(textNode);

        currentIndex++;
        setTimeout(addNextChar, speed);
      }

      // Iniciar após o delay + stagger
      setTimeout(addNextChar, delay + (index * staggerDelay));
    });
  }

  /**
   * Inicializa animações para todos os elementos animados em um container
   * Usado quando conteúdo é inserido dinamicamente (ex: filtros, morph)
   * @param {HTMLElement} container - Container onde buscar elementos
   */
  function initInContainer(container) {
    if (!container) return;

    const selector = '[class*="--animated"]:not(.is-visible)';
    const elements = container.querySelectorAll(selector);
    if (elements.length === 0) return;

    elements.forEach(function(element) {
      if (prefersReducedMotion) {
        element.classList.add('is-visible');
        return;
      }

      function checkAndAnimate() {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        if (isVisible) {
          element.classList.add('is-visible');
          return true;
        }
        return false;
      }

      if (checkAndAnimate()) return;

      const observerOptions = {
        root: null,
        rootMargin: '50px',
        threshold: 0.01
      };

      const observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      observer.observe(element);
    });
  }

  /**
   * Filhos elegíveis para animação em cascata
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  function getCascadeChildren(container) {
    return Array.from(container.children).filter(function(child) {
      return !child.classList.contains('view-product-title') &&
        !child.className.includes('--animated');
    });
  }

  /**
   * Dispara animação em cascata nos filhos diretos
   * @param {HTMLElement} container
   */
  function triggerCascade(container) {
    const children = getCascadeChildren(container);
    if (children.length === 0) return;

    const initialDelay = parseInt(container.getAttribute('data-cascade-initial-delay') || '200', 10);
    const stagger = parseInt(container.getAttribute('data-cascade-stagger') || '100', 10);

    if (prefersReducedMotion) {
      children.forEach(function(child) {
        child.classList.add('cascade-visible');
      });
      return;
    }

    children.forEach(function(child, index) {
      setTimeout(function() {
        if (child.isConnected) {
          child.classList.add('cascade-visible');
        }
      }, initialDelay + index * stagger);
    });
  }

  /**
   * Reseta estado visual da cascata num container
   * @param {HTMLElement} container
   */
  function resetCascadeContainer(container) {
    container.removeAttribute('data-cascade-initialized');
    getCascadeChildren(container).forEach(function(child) {
      child.classList.remove('cascade-visible');
    });
  }

  /**
   * Dispara cascata quando o pai de visibilidade está ativo (ex.: slideshow-slide)
   * @param {HTMLElement} container
   */
  function attemptCascadeTrigger(container) {
    const visibilityParentType = container.getAttribute('data-cascade-visibility-parent');

    if (visibilityParentType) {
      const visibilityParent = container.closest(visibilityParentType);
      if (!visibilityParent || visibilityParent.getAttribute('aria-hidden') !== 'false') {
        return false;
      }
    }

    triggerCascade(container);
    container.setAttribute('data-cascade-initialized', 'true');
    return true;
  }

  /**
   * Observa slide ativo para re-disparar cascata ao trocar de slide
   * @param {HTMLElement} container
   * @param {Element} visibilityParent
   */
  function bindSlideshowCascade(container, visibilityParent) {
    if (container.dataset.cascadeSlideshowBound === 'true') {
      return;
    }

    container.dataset.cascadeSlideshowBound = 'true';

    const observer = new MutationObserver(function() {
      if (visibilityParent.getAttribute('aria-hidden') === 'false') {
        resetCascadeContainer(container);
        attemptCascadeTrigger(container);
      } else {
        resetCascadeContainer(container);
      }
    });

    observer.observe(visibilityParent, {
      attributes: true,
      attributeFilter: ['aria-hidden']
    });

    if (visibilityParent.getAttribute('aria-hidden') === 'false') {
      attemptCascadeTrigger(container);
    }
  }

  /**
   * Inicializa animação em cascata para um container
   * @param {HTMLElement} container
   * @returns {boolean}
   */
  function initCascade(container) {
    if (!container || container.getAttribute('data-cascade-enabled') !== 'true') {
      return false;
    }

    const visibilityParentType = container.getAttribute('data-cascade-visibility-parent');
    if (visibilityParentType) {
      const visibilityParent = container.closest(visibilityParentType);
      if (visibilityParent) {
        bindSlideshowCascade(container, visibilityParent);
        return true;
      }
    }

    if (container.getAttribute('data-cascade-initialized') === 'true') {
      return true;
    }

    const children = getCascadeChildren(container);
    if (children.length === 0) {
      return false;
    }

    if (prefersReducedMotion) {
      attemptCascadeTrigger(container);
      return true;
    }

    const rect = container.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

    if (isVisible) {
      attemptCascadeTrigger(container);
      return true;
    }

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          attemptCascadeTrigger(container);
          observer.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '100px',
      threshold: 0
    });

    observer.observe(container);

    if (typeof MutationObserver !== 'undefined') {
      const mutationObserver = new MutationObserver(function() {
        if (container.getAttribute('data-cascade-initialized') === 'true') {
          resetCascadeContainer(container);
        }
        initCascade(container);
      });

      mutationObserver.observe(container, { childList: true });
    }

    return true;
  }

  /**
   * Inicializa todas as cascatas em um root
   * @param {ParentNode} [root]
   */
  function initAllCascades(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-cascade-enabled="true"]').forEach(function(container) {
      initCascade(container);
    });
  }

  /**
   * Re-tenta cascatas ainda não inicializadas após layout completo
   * @param {number} [attempt]
   */
  function retryUninitializedCascades(attempt) {
    const currentAttempt = typeof attempt === 'number' ? attempt : 0;
    if (currentAttempt >= 30) return;

    document.querySelectorAll('[data-cascade-enabled="true"]').forEach(function(container) {
      if (container.getAttribute('data-cascade-initialized') === 'true') return;

      const children = getCascadeChildren(container);
      if (children.length > 0) {
        initCascade(container);
      }
    });

    setTimeout(function() {
      retryUninitializedCascades(currentAttempt + 1);
    }, 50);
  }

  /**
   * Reseta cascata em um root (editor / section reload)
   * @param {ParentNode} root
   */
  function resetCascadesInRoot(root) {
    if (!root) return;

    root.querySelectorAll('[data-cascade-enabled="true"]').forEach(function(container) {
      container.removeAttribute('data-cascade-slideshow-bound');
      resetCascadeContainer(container);
    });
  }

  function bootstrapCascades(root) {
    initAllCascades(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      bootstrapCascades();
    });
  } else {
    bootstrapCascades();
  }

  window.addEventListener('load', function() {
    retryUninitializedCascades(0);
  });

  document.addEventListener('shopify:section:load', function(event) {
    resetCascadesInRoot(event.target);
    bootstrapCascades(event.target);
  });

  // Exportar funções para uso global
  window.AnimationSystem = {
    init: initAnimation,
    initInContainer: initInContainer,
    initDivider: initDividerAnimation,
    initTypewriter: initTypewriter,
    initTypewriterList: initTypewriterList,
    initCascade: initCascade,
    initAllCascades: initAllCascades,
    resetCascadesInRoot: resetCascadesInRoot
  };

})();

